import { useEffect, useMemo } from 'react'
import { Box } from '@mui/material'
import { useCropData } from '~/crop/crop-list/use-crop-data'
import { useMultiCropSuccessionData } from '~/crop/crop-schedule/hooks/use-multi-crop-succession-data'
import { useMultiCropScheduleData } from '~/crop/crop-schedule/hooks/use-multi-crop-schedule-data'
import { useCropRelationships } from '../use-crop-relationships'
import {
  useMapInspectorContext,
  useMapInspectorContextDispatch,
  setPlotStatusMap,
  setRelationshipCounts,
} from '../context/map-inspector-context'
import { MapCropsTable } from './map-crops-table'

const DETAILED_TO_TIER = {
  'template-incompatible': 'critical',
  'family-incompatible':   'warning',
  'template-companion':    'thriving',
  'family-companion':      'thriving',
  'same-family':           'warning',
  'neutral':               'neutral',
}

const STATUS_PRIORITY = { critical: 3, warning: 2, thriving: 1, neutral: 0 }

export const MapInspectorPanel = () => {
  const { selectedCropId } = useMapInspectorContext()
  const dispatch = useMapInspectorContextDispatch()

  const { crops } = useCropData()
  const { successionMap } = useMultiCropSuccessionData({ crops })
  const { scheduleData } = useMultiCropScheduleData({ crops })
  const { classifyCropDetailed, computeRelationshipCounts } = useCropRelationships({ crops })

  // Compute plotStatusMap: map plot org id → worst tier among its crops
  const computedPlotStatusMap = useMemo(() => {
    const map = new Map()

    for (const crop of (crops || [])) {
      const plotId = crop.organization_id
      if (!plotId) continue

      const detailed = selectedCropId
        ? classifyCropDetailed(selectedCropId, crop.id)
        : 'neutral'
      const status = DETAILED_TO_TIER[detailed] || 'neutral'

      const current = map.get(plotId)
      if (!current || STATUS_PRIORITY[status] > STATUS_PRIORITY[current]) {
        map.set(plotId, status)
      }
    }

    return map
  }, [crops, selectedCropId, classifyCropDetailed])

  // Dispatch plotStatusMap into context whenever it changes
  useEffect(() => {
    dispatch(setPlotStatusMap({ plotStatusMap: computedPlotStatusMap }))
  }, [dispatch, computedPlotStatusMap])

  // Compute org-wide relationship counts and dispatch
  const computedRelationshipCounts = useMemo(
    () => computeRelationshipCounts(crops || []),
    [computeRelationshipCounts, crops]
  )

  useEffect(() => {
    dispatch(setRelationshipCounts({ relationshipCounts: computedRelationshipCounts }))
  }, [dispatch, computedRelationshipCounts])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <MapCropsTable
          crops={crops || []}
          classifyCropDetailed={classifyCropDetailed}
          scheduleData={scheduleData}
          successionMap={successionMap}
        />
      </Box>
    </Box>
  )
}
